export const MAX_UPLOAD_FILE_SIZE = 100;

export const MAX_MESSAGE_ATTACHMENT_COUNT = 3;

// Total across a message's attachments, not a per-file limit.
//
// Attachments travel over the websocket as base64, which inflates them by a
// third, and the server caps a frame at 100MiB - so the 75MiB it accepts is the
// entire frame once encoded, and a message at exactly that limit is dropped by
// the transport before any validation can report why. A megabyte of headroom
// covers the JSON envelope and keeps the limit a round number the error message
// can state exactly.
export const MAX_MESSAGE_ATTACHMENTS_TOTAL_SIZE = 74 * 1024 * 1024;

export const MAX_PROPERTY_DOCUMENT_COUNT = 10;

// Matches how the API reports a stored file's size, so an attachment does not
// change the way it reads once the message comes back from the server.
export const formatAttachmentSize = (bytes: number) =>
  bytes >= 1000 * 1000
    ? `${(bytes / (1000 * 1000)).toFixed(2)} MB`
    : `${(bytes / 1000).toFixed(2)} KB`;

// Must stay in sync with `chatAttachmentAllowedExtensions` in tcc-api: the server
// re-checks the extension and matches the contents against its magic bytes, so
// anything accepted here but missing there is rejected after the upload.
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  "docx",
  "xlsx",
  "doc",
  "xls",
  "pdf",
  "jpeg",
  "jpg",
  "png",
  "heic",
  "heif",
  "gif",
  "txt",
  "csv",
  "rtf",
];

// `accept` attribute for chat attachment inputs, derived from the allowlist so
// the two cannot drift apart.
export const ATTACHMENT_ACCEPT_ATTRIBUTE = ALLOWED_ATTACHMENT_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(",");

// Human-readable allowlist for error copy, e.g. "DOCX, XLSX and PDF".
export const ALLOWED_ATTACHMENT_EXTENSIONS_LABEL = (() => {
  const labels = ALLOWED_ATTACHMENT_EXTENSIONS.map((extension) =>
    extension.toUpperCase()
  );

  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
})();

export const REPORT_FILE_TYPES = {
  PDF: "application/pdf",
  JPEG: "image/jpeg",
  JPG: "image/jpg",
  PNG: "image/png",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  XLS: "application/vnd.ms-excel",
  XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  CSV: "text/csv",
} as const;

export const DEFAULT_FILE_TYPES = [
  REPORT_FILE_TYPES.PDF,
  REPORT_FILE_TYPES.JPEG,
  REPORT_FILE_TYPES.JPG,
  REPORT_FILE_TYPES.DOC,
  REPORT_FILE_TYPES.DOCX,
  REPORT_FILE_TYPES.XLS,
  REPORT_FILE_TYPES.XLSX,
  REPORT_FILE_TYPES.CSV,
] as const;

export const REPORT_FILE_TYPES_ARRAY = [
  REPORT_FILE_TYPES.PDF,
  REPORT_FILE_TYPES.JPEG,
  REPORT_FILE_TYPES.JPG,
  REPORT_FILE_TYPES.PNG,
  REPORT_FILE_TYPES.DOC,
  REPORT_FILE_TYPES.DOCX,
  REPORT_FILE_TYPES.XLS,
  REPORT_FILE_TYPES.XLSX,
  REPORT_FILE_TYPES.CSV,
] as const;

export const FILE_TYPE_EXTENSIONS = {
  [REPORT_FILE_TYPES.PDF]: ".pdf",
  [REPORT_FILE_TYPES.JPEG]: ".jpeg",
  [REPORT_FILE_TYPES.JPG]: ".jpg",
  [REPORT_FILE_TYPES.PNG]: ".png",
  [REPORT_FILE_TYPES.DOC]: ".doc",
  [REPORT_FILE_TYPES.DOCX]: ".docx",
  [REPORT_FILE_TYPES.XLS]: ".xls",
  [REPORT_FILE_TYPES.XLSX]: ".xlsx",
  [REPORT_FILE_TYPES.CSV]: ".csv",
};

export const FILE_TYPE_DISPLAY_NAMES = {
  [REPORT_FILE_TYPES.PDF]: "PDF",
  [REPORT_FILE_TYPES.JPEG]: "JPG",
  [REPORT_FILE_TYPES.JPG]: "JPG",
  [REPORT_FILE_TYPES.PNG]: "PNG",
  [REPORT_FILE_TYPES.DOC]: "DOC",
  [REPORT_FILE_TYPES.DOCX]: "DOCX",
  [REPORT_FILE_TYPES.XLS]: "XLS",
  [REPORT_FILE_TYPES.XLSX]: "XLSX",
  [REPORT_FILE_TYPES.CSV]: "CSV",
};

// Reverse mapping: extension -> MIME type (for fallback validation)
const EXTENSION_TO_MIME_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(FILE_TYPE_EXTENSIONS).map(([mime, ext]) => [
    ext.slice(1).toLowerCase(),
    mime,
  ])
);

/**
 * Check if a file is accepted by MIME type, falling back to extension check.
 * Browsers report inconsistent MIME types for CSV/spreadsheet files, so
 * extension-based validation is used as a fallback.
 */
export const isFileTypeAccepted = (
  file: File,
  validTypes: readonly string[]
): boolean => {
  if ((validTypes as readonly string[]).includes(file.type)) return true;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!ext) return false;
  const mimeForExt = EXTENSION_TO_MIME_TYPE[ext];
  return mimeForExt
    ? (validTypes as readonly string[]).includes(mimeForExt)
    : false;
};

export const getFileTypeDisplayName = (type: string): string => {
  if (type in FILE_TYPE_DISPLAY_NAMES) {
    return FILE_TYPE_DISPLAY_NAMES[
      type as keyof typeof FILE_TYPE_DISPLAY_NAMES
    ];
  }
  return type.split("/")[1].toUpperCase();
};

// Helper function to get file extension for accept attribute
export const getFileTypeExtension = (type: string): string => {
  if (type in FILE_TYPE_EXTENSIONS) {
    return FILE_TYPE_EXTENSIONS[type as keyof typeof FILE_TYPE_EXTENSIONS];
  }
  return `.${type.split("/")[1]}`;
};
